import React, { useState } from 'react';
import { invoke } from '@tauri-apps/api/core';
import { FolderPlus, X } from 'lucide-react';
import Tooltip from '../ui/Tooltip';

/**
 * Settings → MCP → Attachments (card 7de79076): where an agent may upload
 * screenshots and recordings FROM (`media_upload`).
 *
 * "Any folder" is on by default (owner, 11.10.2026); Apiboo's own data
 * folders stay closed either way, and only images and videos are accepted.
 * Turning it ON goes through a native confirm that Rust shows
 * (`mcp_attachment_any_folder_set`) — this page can ask, never grant.
 * With it off, only the folders picked in the OS folder picker, which Rust
 * opens (`mcp_attachment_folder_add`) — this page never sends a path, so
 * nothing in the webview can widen what an agent may read.
 */
export default function McpAttachmentFolders({ t, folders = [], anyFolder = true, onChange }) {
  const [busy, setBusy] = useState(false);

  const run = async (cmd, args) => {
    setBusy(true);
    try {
      onChange(await (args ? invoke(cmd, args) : invoke(cmd)));
    } catch (err) {
      console.warn(`[mcp] ${cmd} failed:`, err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mcp-attach-folders">
      <p className="mcp-attach-folders-desc">
        {t('mcp.attachmentsDesc', "Agents can attach images and videos to task comments and documents. Each file is encrypted, stored in your Apiboo account and counts against your plan's file size and storage limits.")}
      </p>
      <label className="mcp-attach-any">
        <input
          type="checkbox"
          className="checkbox-brand"
          checked={anyFolder}
          disabled={busy}
          onChange={(e) => run('mcp_attachment_any_folder_set', { enabled: e.target.checked })}
        />
        <span>{t('mcp.attachmentsAnyFolder', "From any folder (Apiboo's own data folders stay closed)")}</span>
      </label>
      {anyFolder ? null : (
        <>
          {folders.length === 0 ? (
            <div className="mcp-attach-folders-empty">{t('mcp.attachmentsNone', 'No folder yet — agents cannot attach files.')}</div>
          ) : (
            <ul className="mcp-attach-folders-list">
              {folders.map((f) => (
                <li key={f} className="mcp-attach-folders-item">
                  <span className="mcp-attach-folders-path" title={f}>{f}</span>
                  <Tooltip content={t('mcp.attachmentsRemove', 'Remove')} side="top">
                    <button type="button" className="mcp-attach-folders-remove" aria-label={`${t('mcp.attachmentsRemove', 'Remove')} ${f}`} onClick={() => run('mcp_attachment_folder_remove', { folder: f })}>
                      <X size={13} />
                    </button>
                  </Tooltip>
                </li>
              ))}
            </ul>
          )}
          <button type="button" className="settings-action-btn mcp-attach-folders-add" onClick={() => run('mcp_attachment_folder_add')} disabled={busy}>
            <FolderPlus size={14} /> {t('mcp.attachmentsAdd', 'Add folder…')}
          </button>
        </>
      )}
    </div>
  );
}
